import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtMin } from "@/lib/format";
import { StatCard, SectionTitle } from "@/components/common";
import { AlertTriangle, Package, Truck, Users, ClipboardList, Timer, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

const SEV_STYLES = {
  critical: "border-signal-red/50 bg-signal-red/10 text-signal-red",
  warning: "border-signal-amber/50 bg-signal-amber/10 text-signal-amber",
  info: "border-signal-blue/50 bg-signal-blue/10 text-signal-blue",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (e) {
      setError("Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <p className="text-signal-red" data-testid="dashboard-error">{error}</p>;
  if (!stats) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const attention = stats.attention || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Real-time</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight uppercase">Operations Command</h1>
        </div>
        <p className="text-xs text-muted-foreground font-mono hidden sm:block">Auto-refresh 30s</p>
      </div>

      <div
        className={`rounded-lg border p-4 sm:p-5 ${
          attention.some((a) => a.severity === "critical")
            ? "border-signal-red/60 bg-signal-red/5"
            : attention.length
            ? "border-signal-amber/50 bg-signal-amber/5"
            : "border-signal-green/40 bg-signal-green/5"
        }`}
        data-testid="management-attention-panel"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className={`w-5 h-5 ${attention.length ? "text-signal-red animate-pulse-red" : "text-signal-green"}`} />
          <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">
            Management Attention Required
          </h2>
          <span className="metric text-sm text-muted-foreground ml-auto" data-testid="attention-count">{attention.length}</span>
        </div>
        {attention.length === 0 ? (
          <p className="text-sm text-signal-green font-medium">All clear — no issues need management attention right now.</p>
        ) : (
          <ul className="space-y-2">
            {attention.map((a, i) => (
              <li key={`${a.severity}-${a.message}`} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${SEV_STYLES[a.severity]}`} data-testid={`attention-item-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-widest mt-0.5 shrink-0 w-20">{a.severity}</span>
                <span className="text-foreground/90">{a.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionTitle>Today's Flow</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Bills today" value={stats.bills.total} icon={Package} testid="kpi-bills-today" />
          <StatCard label="Bills packed" value={stats.bills.packed} tone="green" testid="kpi-bills-packed" />
          <StatCard label="Bills shipped" value={stats.bills.shipped} tone="green" testid="kpi-bills-shipped" />
          <StatCard label="Bills pending" value={stats.bills.pending} tone={stats.bills.pending > 0 ? "amber" : "default"} testid="kpi-bills-pending" />
          <StatCard label="Inward done" value={stats.inward.completed} icon={ArrowDownToLine} tone="green" sub={`${stats.inward.total} vehicles`} testid="kpi-inward-done" />
          <StatCard label="Inward pending" value={stats.inward.pending} tone={stats.inward.pending > 0 ? "amber" : "default"} testid="kpi-inward-pending" />
          <StatCard label="Outward done" value={stats.outward.completed} icon={ArrowUpFromLine} tone="green" sub={`${stats.outward.total} dispatches`} testid="kpi-outward-done" />
          <StatCard label="Outward pending" value={stats.outward.pending} tone={stats.outward.pending > 0 ? "amber" : "default"} testid="kpi-outward-pending" />
        </div>
      </div>

      <div>
        <SectionTitle>Manpower & Tasks</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Workers present" value={stats.workers.present} icon={Users} testid="kpi-workers-present" />
          <StatCard label="Working now" value={stats.workers.working} tone="green" testid="kpi-workers-working" />
          <StatCard label="Idle" value={stats.workers.idle} tone={stats.workers.idle > 0 ? "amber" : "default"} testid="kpi-workers-idle" />
          <StatCard label="Active tasks" value={stats.tasks.active} icon={ClipboardList} tone="blue" testid="kpi-active-tasks" />
          <StatCard label="Delayed tasks" value={stats.tasks.delayed} tone={stats.tasks.delayed > 0 ? "red" : "default"} testid="kpi-delayed-tasks" />
          <StatCard label="Avg packing" value={fmtMin(stats.avg.packing)} icon={Timer} testid="kpi-avg-packing" />
          <StatCard label="Avg dispatch" value={fmtMin(stats.avg.shipping)} testid="kpi-avg-shipping" />
          <StatCard label="Avg inward" value={fmtMin(stats.avg.inward)} testid="kpi-avg-inward" />
        </div>
      </div>

      <div>
        <SectionTitle>Today's Worker Productivity</SectionTitle>
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="dashboard-productivity-table">
            <thead>
              <tr className="border-b border-border text-left">
                {["#", "Worker", "Tasks", "Qty Handled", "Productive Hrs", "Qty/Hr"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.productivity.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground text-sm">No completed tasks yet today</td></tr>
              )}
              {stats.productivity.map((p, i) => (
                <tr key={p.user_id} className="border-b border-border/50 hover:bg-accent/40 transition-colors duration-150">
                  <td className="px-4 py-3 metric text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 metric">{p.tasks}</td>
                  <td className="px-4 py-3 metric">{p.qty.toLocaleString()}</td>
                  <td className="px-4 py-3 metric">{p.productive_hrs}</td>
                  <td className="px-4 py-3 metric text-signal-green font-bold">{p.per_hour.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
